// Unified Invoice Template for both preview and PDF generation
// This ensures the preview and downloaded PDF look exactly the same
import { getDiwaliWatermarkCSS, getDiwaliWatermarkHTML } from './diwaliWatermark';

export const generateUnifiedInvoiceHTML = (invoice) => {
  try {
    // Safety check - ensure invoice is valid
    if (!invoice || typeof invoice !== 'object') {
      console.error('Invalid invoice data provided to generateUnifiedInvoiceHTML:', invoice);
      return '<div>Error: Invalid invoice data</div>';
    }

    // Add fallback data if invoice is empty
    const fallbackInvoice = {
    company: { 
      name: 'Sample Company', 
      address: 'Sample Address', 
      mobile: '1234567890', 
      gstin: 'SAMPLE123', 
      email: 'sample@company.com',
      logo: null
    },
    billTo: { name: 'Sample Customer', address: 'Sample Address', state: 'Sample State', mobile: '9876543210' },
    shipTo: { name: 'Sample Customer', address: 'Sample Address' },
    items: [{ description: 'Sample Item', quantity: 1, rate: 100, tax: 18, amount: 118, hsn: 'HSN001' }],
    subtotal: 100,
    totalAmount: 118,
    invoiceNumber: 'INV-001',
    invoiceDate: new Date().toISOString().split('T')[0],
    documentType: 'invoice',
    discount: 0,
    taxDetails: { cgst: 9, sgst: 9, totalTax: 18 },
    amountInWords: 'One Hundred and Eighteen Rupees',
    termsAndConditions: 'Sample terms and conditions',
    bankDetails: { name: 'Sample Bank', accountNo: '1234567890', ifscCode: 'SAMPLE000123', branch: 'Sample Branch' }
  };

  const displayInvoice = invoice && Object.keys(invoice).length > 0 ? invoice : fallbackInvoice;

  const formatDateSlash = (dateStr) => {
    if (!dateStr) return '';
    try {
      const str = String(dateStr).trim();
      const cleanDate = str.includes('T') ? str.split('T')[0] : str;
      const parts = cleanDate.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return str;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return String(dateStr);
    }
  };

  const calculateItemTotal = (item) => {
    const qty = Number(item.quantity || item.qty || 0);
    const rate = Number(item.rate || 0);
    const disc = Number(item.discount || 0);
    const gross = qty * rate;
    const discAmt = (gross * disc) / 100;
    const taxable = Math.max(0, gross - discAmt);
    const taxRate = Number(item.tax || item.taxRate || item.gst || 0);
    const itemTax = (taxable * taxRate) / 100;
    const total = taxable + itemTax;
    return isNaN(total) ? 0 : total;
  };

  const calculateSubtotal = () => {
    const subtotal = displayInvoice.items?.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.rate || 0);
      const discount = item.discount || 0;
      const discountAmount = (baseAmount * discount) / 100;
      const itemTotal = baseAmount - discountAmount;
      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0) || 0;
    return isNaN(subtotal) ? 0 : subtotal;
  };

  const calculateTotalTax = () => {
    const totalTax = displayInvoice.items?.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.rate || 0);
      const discount = item.discount || 0;
      const discountAmount = (baseAmount * discount) / 100;
      const discountedAmount = baseAmount - discountAmount;
      const itemTax = discountedAmount * (item.tax || item.taxRate || 0) / 100;
      return sum + (isNaN(itemTax) ? 0 : itemTax);
    }, 0) || 0;
    return isNaN(totalTax) ? 0 : totalTax;
  };

  const calculateTotalDiscount = () => {
    const totalDiscount = displayInvoice.items?.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.rate || 0);
      const discount = item.discount || 0;
      const discountAmount = (baseAmount * discount) / 100;
      return sum + (isNaN(discountAmount) ? 0 : discountAmount);
    }, 0) || 0;
    return isNaN(totalDiscount) ? 0 : totalDiscount;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const totalTax = calculateTotalTax();
    const grandTotal = subtotal + totalTax;
    return isNaN(grandTotal) ? 0 : grandTotal;
  };

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Invoice ${displayInvoice.invoiceNumber}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Arial', sans-serif;
          line-height: 1.6;
          color: #333;
          background: #f5f5f5;
          padding: 20px;
          margin: 0;
          width: 100%;
          display: flex;
          justify-content: center;
        }
        
        .invoice-container {
          max-width: 210mm !important; /* A4 width */
          width: 210mm !important; /* A4 width */
          min-height: 297mm; /* A4 height */
          margin: 0 auto !important;
          background: white;
          box-shadow: 0 0 10px rgba(0,0,0,0.1) !important;
          border-radius: 8px !important;
          overflow: visible !important;
          padding: 15px;
          box-sizing: border-box;
        }
        
        
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #667eea;
          margin-bottom: 8px;
        }
        
        .company-details {
          font-size: 12px;
          color: #666;
          line-height: 1.4;
        }
        
        .invoice-title {
          font-size: 16px;
          font-weight: bold;
          color: #667eea;
          margin-bottom: 8px;
        }
        
        .invoice-title h1 {
          font-size: 16px;
          font-weight: bold;
          color: #667eea;
          margin: 0;
        }
        
        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
        }
        
        .company-info {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 15px;
        }
        
        .company-logo {
          flex-shrink: 0;
        }
        
        .company-logo img {
          width: 60px;
          height: 60px;
          object-fit: contain;
          border-radius: 5px;
          border: 1px solid #ddd;
        }
        
        .company-text {
          flex: 1;
        }
        
        .invoice-info {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin: 20px 0;
        }
        
        .company-section {
          flex: 1;
        }
        
        .invoice-details {
          flex: 1;
          text-align: right;
        }
        
        .invoice-meta-table {
          margin-left: auto;
          border-collapse: collapse;
        }
        
        .invoice-meta-table td {
          padding: 3px 0 3px 15px;
          font-size: 14px;
          text-align: right;
        }
        
        .meta-label {
          color: #666;
        }
        
        .meta-value {
          font-weight: bold;
          color: #667eea;
        }
        
        .address-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .bill-to, .ship-to {
          padding: 12px;
          background: #f8f9fa;
          border-radius: 5px;
          border-left: 3px solid #667eea;
        }
        
        .section-title {
          font-size: 16px;
          font-weight: bold;
          color: #667eea;
          margin-bottom: 15px;
          text-transform: uppercase;
        }
        
        .client-name {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        .client-details {
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        }
        
        .transport-details {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 5px;
          margin-bottom: 20px;
          border-left: 3px solid #667eea;
        }
        
        .transport-title {
          font-size: 12px;
          font-weight: bold;
          color: #667eea;
          margin-bottom: 10px;
        }
        
        .transport-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          font-size: 10px;
        }
        
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        
        .items-table th {
          background: #667eea;
          color: white;
          padding: 12px 15px;
          text-align: left;
          font-weight: bold;
          font-size: 12px;
        }
        
        .items-table td {
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          font-size: 12px;
        }
        
        .items-table tr:nth-child(even) {
          background: #f8f9fa;
        }
        
        .items-table tr:hover {
          background: #e3f2fd;
        }
        
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-bold { font-weight: bold; }
        
        .totals-section {
          display: flex;
          justify-content: flex-end;
          margin: 20px 0;
        }
        
        .totals-box {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          min-width: 280px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
          padding: 5px 0;
        }
        
        .total-row.grand-total {
          border-top: 2px solid #667eea;
          padding-top: 15px;
          margin-top: 15px;
          font-size: 18px;
          font-weight: bold;
          color: #667eea;
        }
        
        .amount-words {
          background: #fff3cd;
          padding: 12px;
          border-radius: 6px;
          margin: 15px 0;
          border-left: 4px solid #ffc107;
          font-style: italic;
        }
        
        .bank-details {
          background: #d1ecf1;
          padding: 15px;
          border-radius: 8px;
          margin: 15px 0;
          border-left: 4px solid #17a2b8;
        }
        
        .bank-title {
          font-size: 16px;
          font-weight: bold;
          color: #0c5460;
          margin-bottom: 15px;
        }
        
        .terms-conditions {
          background: #f8d7da;
          padding: 15px;
          border-radius: 8px;
          margin: 15px 0;
          border-left: 4px solid #dc3545;
        }
        
        .terms-title {
          font-size: 16px;
          font-weight: bold;
          color: #721c24;
          margin-bottom: 15px;
        }
        
        .signature-section {
          text-align: right;
          margin-top: 15px;
        }
        
        .signature-title {
          font-size: 12px;
          font-weight: bold;
          color: #667eea;
          margin-bottom: 8px;
        }
        
        .signature-box {
          display: inline-flex;
          border: 2px dashed #ccc;
          padding: 15px;
          border-radius: 6px;
          min-width: 180px;
          min-height: 60px;
          align-items: center;
          justify-content: center;
        }
        
        .signature-image {
          max-height: 60px;
          max-width: 100%;
          object-fit: contain;
        }
        
        @media print {
          body { 
            padding: 0; 
            background: white;
          }
          .invoice-container { 
            box-shadow: none; 
            margin: 0;
            max-width: none;
            width: 100%;
            padding: 15mm;
          }
        }

        @media screen and (max-width: 820px) {
          body {
            padding: 8px;
          }
          .invoice-container {
            width: 100% !important;
            max-width: 100% !important;
            min-height: auto;
            padding: 12px !important;
            box-shadow: none !important;
          }
          .header-section {
            flex-direction: column;
            gap: 12px;
          }
          .totals-table {
            width: 100% !important;
          }
        }
        


        ${displayInvoice.theme === 'modern' ? `
          /* Modern Theme Overrides */
          .invoice-title { color: #2b5876; font-size: 20px; text-transform: uppercase; letter-spacing: 1px; }
          .company-name { color: #2b5876; font-size: 22px; }
          .section-title, .transport-title, .bank-title, .terms-title { color: #2b5876; }
          .transport-details { border-left: 4px solid #2b5876; background: #f4f7f6; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
          .bill-to, .ship-to { border-left: 4px solid #2b5876; background: #f4f7f6; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
          .bank-details { border-left: 4px solid #4e4376; background: #f4f7f6; border-radius: 8px; }
          .bank-title { color: #4e4376; }
          .terms-conditions { border-left: 4px solid #e53e3e; background: #fff5f5; border-radius: 8px; }
          .terms-title { color: #c53030; }
          .items-table th { background: linear-gradient(90deg, #2b5876, #4e4376); border-radius: 4px 4px 0 0; }
          .items-table tr:nth-child(even) { background: #f4f7f6; }
          .total-row.grand-total { color: #2b5876; border-top: 2px solid #2b5876; font-size: 14px; }
          .totals-box { border: none; background: #f4f7f6; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
          .signature-title { color: #2b5876; }
        ` : displayInvoice.theme === 'minimalist' ? `
          /* Minimalist Theme Overrides */
          body { color: #000; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; }
          .invoice-title { color: #000; font-weight: normal; font-size: 24px; letter-spacing: 2px; text-transform: uppercase; }
          .company-name { color: #000; font-weight: bold; }
          .section-title, .transport-title, .bank-title, .terms-title { color: #000; font-weight: bold; border-bottom: 1px solid #000; display: inline-block; padding-bottom: 2px; margin-bottom: 12px; }
          .transport-details, .bill-to, .ship-to, .bank-details, .terms-conditions { border-left: none; background: transparent; padding: 0; border: 1px solid #eee; padding: 12px; border-radius: 0; }
          .items-table th { background: #f0f0f0; color: #000; border-top: 1px solid #000; border-bottom: 2px solid #000; }
          .items-table td { border-bottom: 1px solid #f0f0f0; }
          .items-table tr:nth-child(even) { background: transparent; }
          .total-row.grand-total { color: #000; border-top: 1px solid #000; border-bottom: 1px double #000; padding-bottom: 8px; }
          .totals-box { background: transparent; border: none; padding-right: 0; }
          .signature-title { color: #000; font-weight: normal; }
          .amount-words { background: transparent; border: 1px solid #eee; border-left: 3px solid #000; font-style: normal; }
        ` : ''}
        ${getDiwaliWatermarkCSS(displayInvoice.watermarkType || displayInvoice.watermark || 'none')}
      </style>
    </head>
    <body>
      <div class="invoice-container${(['diwali_frame', 'diwali_rangoli'].includes(displayInvoice.watermarkType) || ['diwali_frame', 'diwali_rangoli'].includes(displayInvoice.watermark)) ? ' has-diwali-frame' : (displayInvoice.watermarkType === 'diwali_toran' || displayInvoice.watermark === 'diwali_toran') ? ' has-diwali-toran' : ''}">
        ${getDiwaliWatermarkHTML(displayInvoice.company?.name || displayInvoice.company_name || '', displayInvoice.watermarkType || displayInvoice.watermark || 'none')}
        <!-- Header -->
        <div class="header-section">
          <div class="company-info">
            ${displayInvoice.company?.logo ? `
              <div class="company-logo">
                <img src="${displayInvoice.company.logo}" alt="Company Logo" />
              </div>
            ` : ''}
            <div class="company-text">
              <div class="company-name">${displayInvoice.company?.name || 'Company Name'}</div>
              <div class="company-details">
                ${displayInvoice.company?.address || 'Company Address'}<br>
                ${(displayInvoice.company?.state || displayInvoice.company_state) ? `State: ${displayInvoice.company?.state || displayInvoice.company_state}<br>` : ''}
                Mobile: ${displayInvoice.company?.mobile || 'N/A'} | GSTIN: ${displayInvoice.company?.gstin || 'N/A'}<br>
                Email: ${displayInvoice.company?.email || 'N/A'}
              </div>
            </div>
          </div>
          
          <div class="invoice-details">
            <div class="invoice-title">${displayInvoice.documentType === 'debit_note' ? 'DEBIT NOTE' :
                displayInvoice.documentType === 'credit_note' ? 'CREDIT NOTE' :
                displayInvoice.documentType === 'delivery_challan' ? 'DELIVERY CHALLAN' : 'TAX INVOICE'}</div>
            <table class="invoice-meta-table">
              <tr>
                <td class="meta-label">Invoice No:</td>
                <td class="meta-value">#${displayInvoice.invoiceNumber}</td>
              </tr>
              <tr>
                <td class="meta-label">Date:</td>
                <td class="meta-value" style="color: #333;">${formatDateSlash(displayInvoice.invoiceDate)}</td>
              </tr>
              ${displayInvoice.originalFor ? `
              <tr>
                <td class="meta-label">Original For:</td>
                <td class="meta-value" style="color: #333;">${displayInvoice.originalFor}</td>
              </tr>` : ''}
            </table>
          </div>
        </div>
        
        <!-- Transport Details -->
        ${(displayInvoice.transportDetails && 
           (displayInvoice.transportDetails.vehicleNumber || 
            displayInvoice.transportDetails.vehicleType || 
            displayInvoice.transportDetails.driverName || 
            displayInvoice.transportDetails.driverContact)) ? `
          <div class="transport-details">
            <div class="transport-title">Transport & Delivery Details</div>
            <div class="transport-grid">
              ${displayInvoice.transportDetails.vehicleNumber ? `<div><strong>Vehicle Number:</strong> ${displayInvoice.transportDetails.vehicleNumber}</div>` : ''}
              ${displayInvoice.transportDetails.vehicleType ? `<div><strong>Vehicle Type:</strong> ${displayInvoice.transportDetails.vehicleType}</div>` : ''}
              ${displayInvoice.transportDetails.driverName ? `<div><strong>Driver Name:</strong> ${displayInvoice.transportDetails.driverName}</div>` : ''}
              ${displayInvoice.transportDetails.driverContact ? `<div><strong>Driver Contact:</strong> ${displayInvoice.transportDetails.driverContact}</div>` : ''}
            </div>
          </div>
        ` : ''}
        
        <!-- Address Section -->
        <div class="address-section">
          <div class="bill-to">
            <div class="section-title">Bill To</div>
            <div class="client-name">${displayInvoice.billTo?.name || 'Customer Name'}</div>
            <div class="client-details">
              ${displayInvoice.billTo?.address || 'Customer Address'}<br>
              Mobile: ${displayInvoice.billTo?.mobile || 'N/A'}<br>
              State: ${displayInvoice.billTo?.state || 'N/A'}<br>
              GSTIN: ${displayInvoice.billTo?.gstin || 'N/A'}
            </div>
          </div>
          
          <div class="ship-to">
            <div class="section-title">Ship To</div>
            <div class="client-name">${displayInvoice.shipTo?.name || displayInvoice.billTo?.name || 'Customer Name'}</div>
            <div class="client-details">
              ${displayInvoice.shipTo?.address || displayInvoice.billTo?.address || 'Customer Address'}<br>
              Mobile: ${displayInvoice.shipTo?.mobile || displayInvoice.billTo?.mobile || 'N/A'}<br>
              State: ${displayInvoice.shipTo?.state || displayInvoice.billTo?.state || 'N/A'}<br>
              GSTIN: ${displayInvoice.shipTo?.gstin || displayInvoice.billTo?.gstin || 'N/A'}
            </div>
          </div>
        </div>
        
        <!-- Items Table -->
        ${(() => {
          const totalTaxVal = calculateTotalTax();
          const compStateStr = (displayInvoice.company?.state || displayInvoice.company_state || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
          const destStateStr = (displayInvoice.billTo?.state || displayInvoice.bill_to_state || displayInvoice.shipTo?.state || displayInvoice.ship_to_state || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

          const isInterstate = Boolean(
            displayInvoice.taxDetails?.isInterstate ||
            displayInvoice.isInterstate ||
            (Number(displayInvoice.taxDetails?.igst) > 0) ||
            (Number(displayInvoice.igst) > 0) ||
            (compStateStr && destStateStr && compStateStr !== destStateStr)
          );

          return `
            <table class="items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>HSN</th>
                  <th class="text-center">Qty</th>
                  <th class="text-right">Rate (₹)</th>
                  <th class="text-center">GST (%)</th>
                  <th class="text-center">Discount (%)</th>
                  ${isInterstate ? `
                    <th class="text-right">IGST (₹)</th>
                  ` : `
                    <th class="text-right">CGST (₹)</th>
                    <th class="text-right">SGST (₹)</th>
                  `}
                  <th class="text-right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                ${(displayInvoice.items || []).map(item => {
                  const qty = Number(item.quantity || item.qty || 0);
                  const rate = Number(item.rate || 0);
                  const disc = Number(item.discount || 0);
                  const gross = qty * rate;
                  const discAmt = (gross * disc) / 100;
                  const taxable = Math.max(0, gross - discAmt);
                  const taxRate = Number(item.tax || item.taxRate || item.gst || 0);
                  const itemTax = (taxable * taxRate) / 100;
                  const total = taxable + itemTax;

                  return `
                    <tr>
                      <td>${
                        item.name ||
                        item.description ||
                        item.item_name ||
                        item.product_name ||
                        item.title ||
                        'N/A'
                      }</td>
                      <td>${item.hsn || 'N/A'}</td>
                      <td class="text-center">${item.quantity || item.qty || 0}</td>
                      <td class="text-right">₹${(isNaN(item.rate) ? 0 : item.rate).toFixed(2)}</td>
                      <td class="text-center">${taxRate}%</td>
                      <td class="text-center">${item.discount || 0}%</td>
                      ${isInterstate ? `
                        <td class="text-right">₹${(isNaN(itemTax) ? 0 : itemTax).toFixed(2)}</td>
                      ` : `
                        <td class="text-right">₹${(isNaN(itemTax / 2) ? 0 : itemTax / 2).toFixed(2)}</td>
                        <td class="text-right">₹${(isNaN(itemTax / 2) ? 0 : itemTax / 2).toFixed(2)}</td>
                      `}
                      <td class="text-right text-bold">₹${(isNaN(total) ? 0 : total).toFixed(2)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            
            <!-- Totals -->
            <div class="totals-section">
              <div class="totals-box">
                <div class="total-row">
                  <span>Subtotal:</span>
                  <span>₹${(() => {
                    const subtotal = isNaN(displayInvoice.subtotal) ? calculateSubtotal() : displayInvoice.subtotal;
                    return (isNaN(subtotal) ? 0 : subtotal).toFixed(2);
                  })()}</span>
                </div>
                ${(calculateTotalDiscount() || 0) > 0 ? `
                  <div class="total-row">
                    <span>Total Discount:</span>
                    <span>-₹${(() => {
                      const discount = calculateTotalDiscount();
                      return (isNaN(discount) ? 0 : discount).toFixed(2);
                    })()}</span>
                  </div>
                ` : ''}
                ${totalTaxVal > 0 ? (
                  isInterstate ? `
                    <div class="total-row">
                      <span>IGST:</span>
                      <span>₹${(() => {
                        const igst = displayInvoice.taxDetails?.igst ?? displayInvoice.igst ?? totalTaxVal;
                        return (isNaN(igst) ? 0 : igst).toFixed(2);
                      })()}</span>
                    </div>
                  ` : `
                    <div class="total-row">
                      <span>CGST:</span>
                      <span>₹${(() => {
                        const cgst = displayInvoice.taxDetails?.cgst ?? displayInvoice.cgst ?? (totalTaxVal / 2);
                        return (isNaN(cgst) ? 0 : cgst).toFixed(2);
                      })()}</span>
                    </div>
                    <div class="total-row">
                      <span>SGST:</span>
                      <span>₹${(() => {
                        const sgst = displayInvoice.taxDetails?.sgst ?? displayInvoice.sgst ?? (totalTaxVal / 2);
                        return (isNaN(sgst) ? 0 : sgst).toFixed(2);
                      })()}</span>
                    </div>
                  `
                ) : ''}
          `;
        })()}
            <div class="total-row grand-total">
              <span>Total:</span>
              <span>₹${(() => {
                const total = isNaN(displayInvoice.totalAmount) ? calculateGrandTotal() : displayInvoice.totalAmount;
                return (isNaN(total) ? 0 : total).toFixed(2);
              })()}</span>
            </div>
          </div>
        </div>
        
        <!-- Amount in Words -->
        ${displayInvoice.amountInWords ? `
          <div class="amount-words">
            <strong>Amount in words:</strong> ${displayInvoice.amountInWords}
          </div>
        ` : ''}
        
        <!-- Bank Details -->
        ${displayInvoice.bankDetails ? `
          <div class="bank-details">
            <div class="bank-title">Bank Details</div>
            <div><strong>Bank Name:</strong> ${displayInvoice.bankDetails.name}</div>
            <div><strong>Account No:</strong> ${displayInvoice.bankDetails.accountNo}</div>
            <div><strong>IFSC Code:</strong> ${displayInvoice.bankDetails.ifscCode}</div>
            <div><strong>Branch:</strong> ${displayInvoice.bankDetails.branch}</div>
          </div>
        ` : ''}
        
        <!-- Terms and Conditions -->
        ${displayInvoice.termsAndConditions ? `
          <div class="terms-conditions">
            <div class="terms-title">Terms & Conditions</div>
            <div>${displayInvoice.termsAndConditions}</div>
          </div>
        ` : ''}
        
        <!-- Signature -->
         <div class="signature-section">
           <div class="signature-title">Authorized Signature</div>
           <div class="signature-box">
             ${displayInvoice.signature ? 
               `<img src="${displayInvoice.signature}" alt="Signature" class="signature-image" />` : 
               '<span style="color: #999;">(Digital signature will appear here)</span>'
             }
           </div>
         </div>
      </div>
    </body>
    </html>
  `;
  } catch (error) {
    console.error('Error generating unified invoice HTML:', error);
    return `
      <div style="padding: 20px; color: red; border: 1px solid red; margin: 20px;">
        <h3>Error generating invoice preview</h3>
        <p>There was an error generating the invoice preview. Please try again.</p>
        <p>Error details: ${error.message}</p>
      </div>
    `;
  }
};

